import { Box, CircularProgress, Typography, styled } from "@mui/material"
import type React from "react"
import { useEffect, useRef } from "react"
import { generatePath, useNavigate, useParams } from "react-router"
import { ROUTES } from "../../utils/constants"
import { MainContent } from "../../components/MainContent"
import { PageContainer as StandardPageContainer } from "../../components/PageContainer"
import { extraColors } from "../../utils/theme"
import { ChatContainer } from "./ChatContainer"
import { ChatProvider, useChat } from "./ChatContext"
import { ChatSidebar } from "./ChatSidebar"

const PageLayout = styled(Box)(() => ({
  display: "flex",
  width: "100%",
  height: "calc(100vh - 180px)", // Adjust for header and banners
  backgroundColor: extraColors.dark,
  overflow: "hidden",
}))

const ChatMain = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  position: "relative",
  backgroundColor: "rgba(255, 255, 255, 0.02)",
  borderRadius: "12px",
  border: `1px solid ${extraColors.translucentWhite}`,
  overflow: "hidden",
}))

const ChatPageContent: React.FC = () => {
  const {
    sessions,
    activeSessionId,
    loadSession,
    resetSession,
    deleteSession,
    isLoadingHistory,
    streamingSessions,
  } = useChat()

  const { chatId } = useParams<{ chatId: string }>()
  const navigate = useNavigate()

  const prevChatIdRef = useRef(chatId)

  useEffect(() => {
    if (chatId && chatId !== activeSessionId && !isLoadingHistory) {
      void loadSession(chatId)
    } else if (!chatId && prevChatIdRef.current) {
      resetSession()
    } else if (!chatId && !activeSessionId && !prevChatIdRef.current) {
      resetSession()
    }
    prevChatIdRef.current = chatId
  }, [chatId, activeSessionId, loadSession, resetSession, isLoadingHistory])

  return (
    <MainContent
      headerProps={{
        title: "Wingman",
      }}
      disableLeftGutter
    >
      <StandardPageContainer sx={{ mb: 0 }}>
        <PageLayout>
          <ChatSidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            streamingSessions={streamingSessions}
            onSessionSelect={id => {
              void navigate(generatePath(ROUTES.WINGMAN_CHAT, { chatId: id }))
            }}
            onDeleteSession={id => {
              void deleteSession(id)
              if (id === chatId) {
                void navigate(ROUTES.WINGMAN)
              }
            }}
            onNewChat={() => {
              void navigate(ROUTES.WINGMAN)
            }}
          />
          <Box sx={{ flex: 1, p: 2, height: "100%", overflow: "hidden" }}>
            <ChatMain>
              {isLoadingHistory ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <CircularProgress
                    size={40}
                    sx={{ color: extraColors.status.navyLight }}
                  />
                  <Typography variant="body1" color={extraColors.medium}>
                    Loading conversation...
                  </Typography>
                </Box>
              ) : (
                <ChatContainer />
              )}
            </ChatMain>
          </Box>
        </PageLayout>
      </StandardPageContainer>
    </MainContent>
  )
}

export const ChatPage: React.FC = () => (
  <ChatProvider>
    <ChatPageContent />
  </ChatProvider>
)
